// InstallPWAPrompt.tsx - Smart PWA installation prompt
import React, { useState, useEffect } from 'react';
import { X, Smartphone, Monitor, Download } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';

const InstallPWAPrompt: React.FC = () => {
  const {
    isInstalled,
    canInstall,
    platform,
    showPrompt,
    deferredPrompt,
    install,
    dismissTemporarily,
    dismissPermanently,
    markAsShown,
  } = usePWAInstall();

  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);

  // Show banner after delay
  useEffect(() => {
    if (showPrompt && !isInstalled) {
      console.log('[PWA] Scheduling banner to show in 10 seconds...');
      const timer = setTimeout(() => {
        console.log('[PWA] Showing banner now!');
        setIsVisible(true);
        markAsShown();
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    } else {
      console.log('[PWA] Not showing banner:', { showPrompt, isInstalled });
    }
  }, [showPrompt, isInstalled]);

  // Don't auto-hide - let user interact with it
  // Removed auto-dismiss timer for better visibility

  const handleInstall = async () => {
    if (platform === 'ios' || !deferredPrompt) {
      // Show instructions for iOS or if native prompt unavailable
      setShowInstructions(true);
    } else {
      // Trigger native prompt for Android/Desktop
      const success = await install();
      if (success) {
        setJustInstalled(true);
        setTimeout(() => {
          setIsVisible(false);
          setJustInstalled(false);
        }, 3000);
      }
    }
  };

  const handleDismissTemp = () => {
    dismissTemporarily();
    setIsVisible(false);
  };

  const handleDismissPermanent = () => {
    dismissPermanently();
    setIsVisible(false);
  };

  // Don't render if installed or shouldn't show
  if (isInstalled || !showPrompt) {
    return null;
  }

  // Success celebration
  if (justInstalled) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3">
          <span className="text-2xl">🎉</span>
          <div>
            <p className="font-bold">Thanks for installing WHIPT!</p>
            <p className="text-sm text-green-100">Find us on your home screen</p>
          </div>
        </div>
      </div>
    );
  }

  // Instructions Modal
  if (showInstructions) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-b from-purple-950 to-purple-900 border border-purple-500/30 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-scale-in">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-amber-300" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Install WHIPT</h3>
                <p className="text-sm text-purple-200">Add to Home Screen</p>
              </div>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="text-purple-300 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {platform === 'ios' ? (
              <>
                <p className="text-purple-100 text-sm">On iPhone/iPad:</p>
                <ol className="space-y-3 text-sm text-purple-200">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">1.</span>
                    <span>Tap the <strong>Share button</strong> <svg className="inline w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/></svg> at the bottom</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">2.</span>
                    <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">3.</span>
                    <span>Tap <strong>"Add"</strong> in the top right</span>
                  </li>
                </ol>
              </>
            ) : platform === 'android' ? (
              <>
                <p className="text-purple-100 text-sm">On Android:</p>
                <ol className="space-y-3 text-sm text-purple-200">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">1.</span>
                    <span>Tap the <strong>three dots menu</strong> (⋮) in the top right</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">2.</span>
                    <span>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">3.</span>
                    <span>Tap <strong>"Add"</strong> or <strong>"Install"</strong></span>
                  </li>
                </ol>
              </>
            ) : (
              <>
                <p className="text-purple-100 text-sm">On Desktop:</p>
                <ol className="space-y-3 text-sm text-purple-200">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">1.</span>
                    <span>Look for the <strong>install icon</strong> ⊕ in your browser's address bar</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">2.</span>
                    <span>Click it and select <strong>"Install"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">3.</span>
                    <span>WHIPT will open in its own window</span>
                  </li>
                </ol>
              </>
            )}

            <div className="bg-purple-800/30 rounded-lg p-3 mt-4">
              <p className="text-xs text-purple-200">
                <strong className="text-amber-300">Benefits:</strong> Faster access, works offline, full-screen experience
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(false)}
            className="w-full mt-6 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:brightness-110 transition-all"
          >
            Got it!
          </button>
        </div>
      </div>
    );
  }

  // Main banner
  return (
    <div
      className={`fixed bottom-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:max-w-2xl z-[9999] transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'
      }`}
      style={{ marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
    >
      <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 border-2 border-purple-500/30 rounded-2xl shadow-2xl p-4 flex items-center gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
          {platform === 'desktop' ? (
            <Monitor className="w-6 h-6 text-amber-300" />
          ) : (
            <Smartphone className="w-6 h-6 text-amber-300" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-sm md:text-base flex items-center gap-2">
            {platform === 'desktop' ? '💻 Install WHIPT' : '📱 Add WHIPT to Home Screen'}
          </h3>
          <p className="text-purple-200 text-xs md:text-sm">
            {platform === 'desktop' 
              ? 'Quick access, works offline, dedicated window'
              : 'Works like a native app - no browser needed!'
            }
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg hover:brightness-110 transition-all flex items-center gap-1 whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Install</span>
          </button>
          
          <button
            onClick={handleDismissTemp}
            className="px-3 py-2 text-purple-300 hover:text-white text-sm transition-colors whitespace-nowrap"
          >
            Later
          </button>

          <button
            onClick={handleDismissPermanent}
            className="text-purple-400 hover:text-purple-200 transition-colors"
            title="Don't show again"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWAPrompt;
